/** Modal dialog for adding a new server. */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { ServerType } from "../../types";
import { serversApi } from "../../api/client";

interface AddServerModalProps {
  open: boolean;
  onClose: () => void;
  onServerCreated: (joinScript?: string) => void;
}

/** Add server modal with form and join script display. */
export default function AddServerModal({
  open,
  onClose,
  onServerCreated,
}: AddServerModalProps) {
  const [hostname, setHostname] = useState("");
  const [serverType, setServerType] = useState<ServerType>(ServerType.KAMAILIO);
  const [hostnameError, setHostnameError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [joinToken, setJoinToken] = useState("");
  const [showScript, setShowScript] = useState(false);
  const [scriptCopied, setScriptCopied] = useState(false);

  /** Validate hostname input. */
  const validateHostname = (value: string): boolean => {
    if (value.length < 3) {
      setHostnameError("Hostname must be at least 3 characters");
      return false;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/i.test(value)) {
      setHostnameError("Invalid hostname format");
      return false;
    }
    setHostnameError("");
    return true;
  };

  /** Handle hostname input change. */
  const handleHostnameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHostname(value);
    if (value.length > 0) {
      validateHostname(value);
    } else {
      setHostnameError("");
    }
  };

  /** Handle form submission. */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateHostname(hostname)) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await serversApi.create({
        hostname,
        server_type: serverType,
      });

      setJoinToken(response.data.join_token);
      setShowScript(true);
      setScriptCopied(false);
    } catch (err: any) {
      console.error("Failed to create server:", err);
      setError(
        err.response?.data?.detail || "Failed to create server. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  /** Handle dialog close. */
  const handleClose = () => {
    if (!loading) {
      setHostname("");
      setServerType(ServerType.KAMAILIO);
      setHostnameError("");
      setError("");
      setJoinToken("");
      setShowScript(false);
      setScriptCopied(false);
      onClose();
    }
  };

  /** Copy join script to clipboard. */
  const handleCopyScript = () => {
    const script = generateJoinScript(hostname, serverType, joinToken);
    navigator.clipboard.writeText(script);
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  };

  /** Generate join script for display. */
  const generateJoinScript = (host: string, type: ServerType, token: string): string => {
    const controlPlaneUrl = window.location.origin.replace("3000", "8000");
    const generatedAt = new Date().toISOString();
    return `#!/bin/bash
# Softswitch Control Plane Join Script
# Server: ${host}
# Type: ${type}
# Generated: ${generatedAt}

set -e

SERVER_ID="${token}"
SERVER_TYPE="${type}"
CONTROL_PLANE_URL="${controlPlaneUrl}"

echo "==> Joining Softswitch Control Plane"
echo "    Server ID: $SERVER_ID"
echo "    Server Type: $SERVER_TYPE"
echo "    Control Plane: $CONTROL_PLANE_URL"
echo ""

# Download agent binary
wget -q -O /tmp/softswitch-agent \${CONTROL_PLANE_URL}/api/v1/agents/binary
chmod +x /tmp/softswitch-agent

# Install agent
/tmp/softswitch-agent install \\\\
    --server-id="\${SERVER_ID}" \\\\
    --server-type="\${SERVER_TYPE}" \\\\
    --control-plane="\${CONTROL_PLANE_URL}"

# Start service
systemctl enable softswitch-agent
systemctl start softswitch-agent

echo "==> Server joined successfully!"`;
  };

  /** Handle done after viewing script. */
  const handleDone = () => {
    onServerCreated(generateJoinScript(hostname, serverType, joinToken));
    handleClose();
  };

  const isFormValid = hostname.length >= 3 && !hostnameError;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {!showScript ? (
        <>
          <DialogTitle>Add New Server</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                autoFocus
                margin="dense"
                label="Hostname"
                fullWidth
                variant="outlined"
                value={hostname}
                onChange={handleHostnameChange}
                error={!!hostnameError}
                helperText={hostnameError || "Server hostname (e.g., node-01)"}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth margin="dense">
                <InputLabel>Server Type</InputLabel>
                <Select
                  value={serverType}
                  label="Server Type"
                  onChange={(e) => setServerType(e.target.value as ServerType)}
                  disabled={loading}
                >
                  <MenuItem value={ServerType.KAMAILIO}>Session Border / Routing</MenuItem>
                  <MenuItem value={ServerType.FREESWITCH}>Media & Call Handling</MenuItem>
                  <MenuItem value={ServerType.CGRATES}>Billing & Rating</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!isFormValid || loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {loading ? "Creating..." : "Create Server"}
              </Button>
            </DialogActions>
          </form>
        </>
      ) : (
        <>
          <DialogTitle>Join Script Generated</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              Server created successfully! Copy the join script below and run it on your
              server to complete the registration.
            </Alert>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Server:</strong> {hostname} ({serverType})
            </Typography>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mt: 2,
                backgroundColor: "grey.50",
                maxHeight: 300,
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Join Script
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleCopyScript}
                  color={scriptCopied ? "success" : "default"}
                 aria-label="Copy">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                  m: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {generateJoinScript(hostname, serverType, joinToken)}
              </Typography>
            </Paper>

            {scriptCopied && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Script copied to clipboard!
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDone} variant="contained">
              Done
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
